import { html, cx, useState, useEffect, useCallback } from '../h.js'
import { Icon } from '../icons.js'
import { t, plural } from '../i18n.js'
import { shortId, roleLabel, fileSize, copyText, listTime } from '../format.js'
import { Avatar, Switch, SettingRow, Empty, useMenu, useToast, CopyField, Badges, ConfirmDialog } from '../components.js'
import { useApp, useStore } from '../store.js'
import { useAttachment, thumbUrl, AUTO_LOAD_MAX } from './message.js'
import { useBotCard } from '../use-bot.js'
import { ROOM_TYPE, ROLE, ACTION, KIND, formatUsername } from '@pine/core'

/**
 * Room info: members and their roles, invite links, shared media, and the
 * settings an admin can change. Everything here writes ops into the room, so
 * a permission change reaches other members the same way a message does.
 */
export function InfoPanel () {
  const store = useStore()
  const roomId = store.get('roomId')
  const [tab, setTab] = useState('info')
  const [room, setRoom] = useState(null)
  const [meta, setMeta] = useState(null)
  const [members, setMembers] = useState([])
  const [myRole, setMyRole] = useState(null)

  const refresh = useCallback(async (r) => {
    if (!r) return
    try {
      const [m, list, role] = await Promise.all([r.view.meta(), r.view.members(), r.myRole()])
      setMeta(m)
      setMembers(list)
      setMyRole(role)
    } catch {
      // The room closed under the open panel (left, deleted, or the app
      // shutting down). Uncaught, this surfaced as SESSION_CLOSED in the
      // error log every time a chat was closed with its panel open.
    }
  }, [])

  useEffect(() => {
    let alive = true
    if (!roomId) return
    store.client.openRoom(roomId).then((r) => {
      if (!alive) return
      setRoom(r)
      refresh(r)
      const off = r.on('update', () => refresh(r))
      return off
    })
    return () => { alive = false }
  }, [roomId, store, refresh])

  if (!room || !meta) return html`<aside class="panel"></aside>`

  const summary = store.client.getSummary(roomId) || {}
  const isChannel = meta.type === ROOM_TYPE.CHANNEL
  const isDM = meta.type === ROOM_TYPE.DM
  const canAdmin = myRole !== null && myRole >= ROLE.ADMIN

  return html`
    <aside class="panel">
      <header class="panel-head">
        <button class="icon-btn" onClick=${() => store.set({ panel: false, mobileView: 'chat' })}>
          ${Icon.close(19)}
        </button>
        <h2 class="grow" style="font-size:var(--text-lg);font-weight:600">${t('panel.info')}</h2>
      </header>

      <div class="tabs">
        ${[['info', t('panel.info')], ['members', t('panel.members')], ['media', t('panel.media')]]
          .filter(([id]) => !(id === 'members' && isDM))
          .map(([id, label]) => html`
            <button key=${id} class=${cx('tab', tab === id && 'active')}
                    onClick=${() => setTab(id)}>${label}</button>`)}
      </div>

      <div class="panel-body">
        ${tab === 'info' && html`
          <${InfoTab} room=${room} meta=${meta} members=${members} summary=${summary}
            canAdmin=${canAdmin} isChannel=${isChannel} isDM=${isDM} store=${store} />`}
        ${tab === 'members' && html`
          <${MembersTab} room=${room} members=${members} myRole=${myRole} meta=${meta} />`}
        ${tab === 'media' && html`<${MediaTab} room=${room} store=${store} />`}
      </div>
    </aside>`
}

function InfoTab ({ room, meta, members, summary, canAdmin, isChannel, isDM, store }) {
  const toast = useToast()
  const [invites, setInvites] = useState([])
  const [busy, setBusy] = useState(false)
  const [editingName, setEditingName] = useState(false)
  const [name, setName] = useState(meta.name || '')
  const [peer, setPeer] = useState(null)

  useEffect(() => {
    store.client.listInvites(room.id).then(setInvites).catch(() => {})
  }, [room.id, store])

  // For a one-to-one chat, the panel is really the other person's card: their
  // name, photo, bio and @username — the same fields they filled in on their
  // own settings screen, which is where they were meant to end up.
  const peerAccount = summary && summary.peer
  const botCard = useBotCard(store, isDM ? peerAccount : null)
  useEffect(() => {
    if (!isDM || !peerAccount) { setPeer(null); return }
    let live = true
    ;(async () => {
      const [member, contact, badges] = await Promise.all([
        room.view.member(peerAccount).catch(() => null),
        store.client.getContact(peerAccount).catch(() => null),
        store.client.badgesFor(peerAccount).catch(() => [])
      ])
      let username = (contact && contact.username) || ''
      if (!username && store.client.registry && store.client.registry.enabled) {
        const rev = await store.client.registry.reverse(peerAccount).catch(() => null)
        username = (rev && rev.username) || ''
      }
      if (!live) return
      setPeer({
        account: peerAccount,
        name: (member && member.name) || (contact && contact.name) || '',
        avatar: (member && member.avatar) || (contact && contact.avatar) || null,
        bio: (member && member.bio) || '',
        username,
        badges
      })
    })()
    return () => { live = false }
  }, [isDM, peerAccount, room, store])

  async function createInvite () {
    setBusy(true)
    try {
      const invite = await store.client.createInvite(room.id, {})
      setInvites(await store.client.listInvites(room.id))
      await copyText(invite.link())
      toast(t('action.copied'))
    } catch (err) {
      toast(err.message, { error: true })
    } finally {
      setBusy(false)
    }
  }

  async function saveName () {
    setEditingName(false)
    if (name.trim() === meta.name) return
    try {
      await room.setMeta({ name: name.trim() })
    } catch (err) {
      toast(err.message, { error: true })
    }
  }

  async function toggleSetting (key, value) {
    try {
      await room.setMeta({ settings: { [key]: value } })
    } catch (err) {
      toast(err.message, { error: true })
    }
  }

  // Never meta.name for a one-to-one chat: that is shared, so it holds one of
  // the two names and shows the wrong one to whichever person it is not.
  const dmName = (peer && peer.name) || (summary && summary.name) ||
    (peerAccount && shortId(peerAccount)) || ''

  return html`
    <div>
      <div class="panel-hero">
        ${isDM
          ? html`
            <${Avatar} name=${dmName} id=${peerAccount || room.id} src=${peer && peer.avatar} size="xl" />
            <h3>${dmName}${Badges({ badges: peer && peer.badges, size: 18 })}</h3>
            ${peer && peer.username && html`<div class="dim small mono">${formatUsername(peer.username)}</div>`}
            ${peer && peer.bio && !botCard && html`<div class="panel-bio">${peer.bio}</div>`}
            ${botCard && html`
              <div class="bot-card">
                ${botCard.about && html`<div class="panel-bio">${botCard.about}</div>`}
                ${botCard.commands.length > 0 && html`
                  <div class="bot-commands">
                    ${botCard.commands.map((c) => html`
                      <div key=${c.command} class="bot-command">
                        <span class="command-name">/${c.command}</span>
                        <span class="dim small">${c.description}</span>
                      </div>`)}
                  </div>`}
                <button class="btn btn-soft btn-sm" style="margin-top:10px"
                        onClick=${() => store.showDialog({ type: 'add-to-group', account: peerAccount, name: dmName })}>
                  ${Icon.userPlus(16)} ${t('bot.addToGroup')}
                </button>
              </div>`}
            ${peerAccount && html`
              <div class="panel-id"><${CopyField} label=${t('settings.yourId')} value=${peerAccount} /></div>`}`
          : html`
            <${Avatar} name=${meta.name} id=${room.id} src=${meta.avatar}
              size="xl" square=${isChannel} />
            ${editingName
              ? html`
                <input class="input" value=${name} autofocus
                       onInput=${(e) => setName(e.target.value)}
                       onBlur=${saveName}
                       onKeyDown=${(e) => { if (e.key === 'Enter') saveName() }} />`
              : html`
                <h3 onClick=${() => canAdmin && setEditingName(true)}
                    style=${canAdmin ? 'cursor:text' : ''}>${meta.name || shortId(room.id)}</h3>`}
            <div class="dim small">
              ${plural('members', members.length)}
              ${meta.description && html`<div style="margin-top:6px">${meta.description}</div>`}
            </div>`}
      </div>

      <div class="divider"></div>

      <div class="section-title">${t('panel.notifications')}</div>
      <${SettingRow} icon=${Icon.bellOff(18)} title=${t('panel.mute')}
        right=${html`<${Switch} checked=${!!summary.muted}
          onChange=${(v) => store.client.setRoomFlags(room.id, { muted: v })} />`} />
      <${SettingRow} icon=${Icon.pin(18)} title=${t('panel.pinChat')}
        right=${html`<${Switch} checked=${!!summary.pinned}
          onChange=${(v) => store.client.setRoomFlags(room.id, { pinned: v })} />`} />
      <${SettingRow} icon=${Icon.archive(18)} title=${t('panel.archive')}
        right=${html`<${Switch} checked=${!!summary.archived}
          onChange=${(v) => store.client.setRoomFlags(room.id, { archived: v })} />`} />

      ${!isDM && html`
        <div>
          <div class="divider"></div>
          <div class="section-title">${t('panel.invite')}</div>
          ${invites.filter((i) => !i.revoked).map((invite) => html`
            <div key=${invite.code} style="padding:0 16px 12px">
              <${CopyField} value=${'https://papi.olshub.xyz/i/' + invite.code} />
            </div>`)}
          <${SettingRow} icon=${Icon.link(18)} title=${t('panel.newInvite')}
            onClick=${busy ? null : createInvite}
            right=${busy ? html`<div class="spinner"></div>` : Icon.plus(17)} />
        </div>`}

      ${canAdmin && !isDM && html`
        <div>
          <div class="divider"></div>
          <div class="section-title">${t('panel.permissions')}</div>
          <${SettingRow} icon=${Icon.eye(18)} title=${t('panel.readOnly')}
            right=${html`<${Switch} checked=${!!meta.settings.readOnly}
              onChange=${(v) => toggleSetting('readOnly', v)} />`} />
          <${SettingRow} icon=${Icon.userPlus(18)} title=${t('panel.membersCanInvite')}
            right=${html`<${Switch} checked=${meta.settings.membersCanInvite !== false}
              onChange=${(v) => toggleSetting('membersCanInvite', v)} />`} />
          <${SettingRow} icon=${Icon.smile(18)} title=${t('panel.reactions')}
            right=${html`<${Switch} checked=${meta.settings.reactionsEnabled !== false}
              onChange=${(v) => toggleSetting('reactionsEnabled', v)} />`} />
        </div>`}

      <div class="divider"></div>
      ${isDM && peerAccount && html`<${BlockRow} store=${store} account=${peerAccount} name=${dmName} />`}
      ${isDM && peerAccount && html`
        <${SettingRow} icon=${Icon.flag(18)} title=${t('report.user')} sub=${t('report.userSub')}
          onClick=${() => store.showDialog({
            type: 'report', kind: 'user', account: peerAccount, roomId: room.id
          })} />`}
      <${SettingRow} icon=${Icon.logout(18)} title=${t('panel.leave')} danger
        onClick=${() => store.showDialog({ type: 'leave', roomId: room.id })} />

      <div class="divider"></div>
      <div class="section-title">${t('restore.section')}</div>
      <${RestoreRow} room=${room} store=${store} />
      <div style="height:24px"></div>
    </div>`
}

/**
 * Ask the other members for what this device is missing.
 *
 * Always here, at the bottom, whether or not anything is wrong — it was
 * previously only on a chat that had come back as a name, which is exactly the
 * case where somebody is least likely to go looking for it, and it was
 * unfindable the rest of the time.
 *
 * What it asks for depends on what is missing. A chat restored from the vault
 * opens and reads on its own, but a device new to the room writes from a core
 * no member has authorised, so it can read and not reply; a chat restored from
 * a list with no vault behind it has no key at all. Both are the same request
 * and the same answer — an invite from somebody who is in the room.
 */
/**
 * Block or unblock the other person of a one-to-one chat. What a block does
 * is the core's to say (client.blockContact); this asks first, because it is
 * quiet — the other side is not told — and easy to forget about.
 */
function BlockRow ({ store, account, name }) {
  const toast = useToast()
  const [blocked, setBlocked] = useState(() => store.client.isBlocked(account))
  const [asking, setAsking] = useState(false)
  const who = name || shortId(account)

  async function set (on) {
    setAsking(false)
    try {
      await store.client.blockContact(account, on)
      setBlocked(on)
      toast(on ? t('block.done', { name: who }) : t('block.undone', { name: who }))
    } catch (err) {
      toast(err.message || t('err.generic'), { error: true })
    }
  }

  return html`
    <${SettingRow} icon=${Icon.block(18)} danger=${!blocked}
      title=${blocked ? t('contacts.unblock') : t('contacts.block')}
      sub=${blocked ? t('block.blockedSub') : t('block.sub')}
      onClick=${() => (blocked ? set(false) : setAsking(true))} />
    ${asking && html`
      <${ConfirmDialog} title=${t('contacts.block') + ' — ' + who} danger
        message=${t('block.confirm', { name: who })} confirmLabel=${t('contacts.block')}
        onConfirm=${() => set(true)} onCancel=${() => setAsking(false)} />`}`
}

function RestoreRow ({ room, store }) {
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  const asking = busy || store.client.isRestoring(room.id)

  async function restore () {
    setBusy(true)
    try {
      const { asked, already } = await store.client.restoreRoom(room.id)
      if (already) toast(t('restore.nothingMissing'))
      else if (asked === 0) toast(t('restore.nobodyOnline'), { error: true })
      else toast(t('restore.asked', { n: asked }))
    } catch (err) {
      toast(err.message || t('err.generic'), { error: true })
    } finally {
      setBusy(false)
    }
  }

  return html`
    <${SettingRow} icon=${Icon.database(18)} title=${t('restore.action')}
      sub=${t('restore.sectionHint')}
      onClick=${asking ? null : restore}
      right=${asking ? html`<div class="spinner"></div>` : Icon.refresh(17)} />`
}

function MembersTab ({ room, members, myRole, meta }) {
  const store = useApp()
  const menu = useMenu()
  const toast = useToast()

  function memberMenu (member) {
    const canManage = myRole !== null && myRole > member.role && myRole >= ROLE.ADMIN
    const items = [
      {
        label: t('panel.copyId'),
        icon: Icon.copy(17),
        onSelect: async () => { await copyText(member.account); toast(t('action.copied')) }
      },
      member.account !== room.me && {
        label: t('contacts.message'),
        icon: Icon.chat(17),
        onSelect: () => store.showDialog({ type: 'direct', peer: member.account, name: member.name })
      }
    ]

    if (canManage) {
      items.push({ separator: true })
      if (member.role < ROLE.MODERATOR) {
        items.push({
          label: t('panel.makeModerator'),
          icon: Icon.shield(17),
          onSelect: () => room.setRole(member.account, ROLE.MODERATOR).catch((e) => toast(e.message, { error: true }))
        })
      }
      if (member.role < ROLE.ADMIN && myRole >= ROLE.OWNER) {
        items.push({
          label: t('panel.makeAdmin'),
          icon: Icon.crown(17),
          onSelect: () => room.setRole(member.account, ROLE.ADMIN).catch((e) => toast(e.message, { error: true }))
        })
      }
      if (member.role > ROLE.MEMBER) {
        items.push({
          label: t('panel.makeMember'),
          icon: Icon.users(17),
          onSelect: () => room.setRole(member.account, ROLE.MEMBER).catch((e) => toast(e.message, { error: true }))
        })
      }
      items.push({
        label: t('panel.removeMember'),
        icon: Icon.block(17),
        danger: true,
        onSelect: () => room.kick(member.account).catch((e) => toast(e.message, { error: true }))
      })
    }
    return items.filter(Boolean)
  }

  const canInvite = myRole !== null &&
    (meta.settings.membersCanInvite !== false || myRole >= ROLE.ADMIN)

  return html`
    <div>
      ${canInvite && html`
        <${SettingRow} icon=${Icon.userPlus(18)} title=${t('panel.invite')}
          onClick=${() => store.showDialog({ type: 'invite', roomId: room.id })} />`}
      <div class="section-title">${plural('members', members.length)}</div>
      ${members.map((member) => html`
        <button key=${member.account} class="list-row"
                onClick=${(e) => menu.open(e, memberMenu(member))}>
          <${Avatar} name=${member.name} id=${member.account} src=${member.avatar} size="sm"
            online=${room.presence.has(member.account)} />
          <span class="body">
            <span class="title">${member.name || shortId(member.account)}
              ${member.account === room.me && html`<span class="dim small"> · ${t('chats.you')}</span>`}</span>
            <span class="sub">${roleLabel(member.role)}</span>
          </span>
          ${member.role >= ROLE.ADMIN && html`
            <span class="glyph" style="color:var(--accent)">
              ${member.role === ROLE.OWNER ? Icon.crown(16) : Icon.shield(16)}
            </span>`}
        </button>`)}
      ${menu.node}
    </div>`
}

function MediaTab ({ room, store }) {
  const [items, setItems] = useState([])
  const [files, setFiles] = useState([])

  useEffect(() => {
    let alive = true
    Promise.all([
      room.view.media({ kinds: [KIND.IMAGE, KIND.VIDEO, KIND.GIF] }),
      room.view.media({ kinds: [KIND.FILE, KIND.AUDIO] })
    ]).then(([media, docs]) => {
      if (!alive) return
      setItems(media)
      setFiles(docs)
    }).catch(() => {})
    return () => { alive = false }
  }, [room])

  if (!items.length && !files.length) {
    return html`<${Empty} icon=${Icon.image(34)} message=${t('panel.noMedia')} />`
  }

  return html`
    <div>
      ${items.length > 0 && html`
        <div class="media-grid">
          ${items.map((message) => html`
            <${MediaThumb} key=${message.id} room=${room} message=${message} store=${store} />`)}
        </div>`}

      ${files.length > 0 && html`
        <div>
          <div class="section-title">${t('panel.files')}</div>
          ${files.map((message) => {
            const a = message.attachments[0] || {}
            return html`
              <div key=${message.id} class="list-row">
                <span class="glyph">${Icon.file(18)}</span>
                <span class="body">
                  <span class="title truncate">${a.name || 'file'}</span>
                  <span class="sub">${fileSize(a.size)} · ${listTime(message.timestamp)}</span>
                </span>
              </div>`
          })}
        </div>`}
    </div>`
}

function MediaThumb ({ room, message, store }) {
  const attachment = message.attachments[0]
  const video = (attachment.mime || '').startsWith('video/')
  // A video's bytes are not a picture; its tile is the preview it was sent
  // with. Large files wait for a tap here as they do in the chat.
  const { url } = useAttachment(room, attachment, { auto: (attachment.size || 0) <= AUTO_LOAD_MAX })
  const preview = thumbUrl(attachment)
  const still = video ? preview : (url || preview)
  return html`
    <button onClick=${() => url && store.openLightbox({ src: url, mime: attachment.mime, name: attachment.name })}>
      ${still
        ? html`<img src=${still} alt="" loading="lazy" class=${cx(still === preview && !video && 'blurred')} />`
        : html`<div class="skeleton" style="width:100%;height:100%"></div>`}
      ${video && html`<span class="media-play">${Icon.play(18)}</span>`}
    </button>`
}
